// Contas a Pagar e a Receber (issue #16). As duas são o mesmo agregado,
// separadas pela `direcao` (ADR-0011).
//
// O ponto central: **Pagamento é entidade, não flag**. Uma Conta pode ter
// vários, e é assim que pagamento parcial existe. A **Situação de Pagamento**
// não é campo — deriva da soma dos Pagamentos, do mesmo jeito que a Situação
// do Lote deriva dos fatos (ADR-0005). Não há `pago: boolean` em lugar nenhum.

import { newId, type Id } from "./ids.ts";
import { isDataISO, type DataISO } from "./datas.ts";
import type { Centavos } from "./money.ts";
import {
  DIRECOES_CONTA,
  FONTES_CONTA,
  type Conta,
  type DirecaoConta,
  type FonteConta,
  type ItemConta,
  type Pagamento,
  type Snapshot,
} from "./snapshot.ts";

export type { Conta, ItemConta, Pagamento, DirecaoConta, FonteConta };
export { DIRECOES_CONTA, FONTES_CONTA };

/** Ponto de uma Conta, derivado dos Pagamentos. Nunca gravado. */
export type SituacaoPagamento = "pendente" | "parcial" | "quitada";

export class ErroConta extends Error {}

interface DadosContaBase {
  descricao: string;
  vencimento: DataISO;
  categoriaId: Id;
  /** Só faz sentido em Conta a Receber. */
  fonte: FonteConta | null;
  clienteId: Id | null;
  /** A Fatura que gerou esta Conta (ADR-0011). `null` = criada à mão. */
  faturaId: Id | null;
  fornecedor?: string | null;
  observacao?: string;
  itens?: ItemConta[];
}

/**
 * Conta a Pagar (#87): o valor é **calculado** — avulso + itens —, nunca
 * informado pronto; o avulso fica gravado e não se perde ao editar.
 */
export interface DadosContaPagar extends DadosContaBase {
  direcao: "pagar";
  /** Valor fora dos itens; `null` = só os itens. */
  avulsoCentavos: Centavos | null;
}

/** Conta a Receber: o valor é o informado (ou o da Fatura — ADR-0011). */
export interface DadosContaReceber extends DadosContaBase {
  direcao: "receber";
  valorCentavos: Centavos;
}

export type DadosConta = DadosContaPagar | DadosContaReceber;

/** Subtotal de um item: quantidade × valor unitário, arredondado ao centavo. */
export function subtotalItem(it: Pick<ItemConta, "quantidade" | "valorUnitarioCentavos">): Centavos {
  return Math.round(it.quantidade * it.valorUnitarioCentavos);
}

/** Total da Conta a Pagar: avulso + soma dos itens — a mesma conta na tela e na gravação. */
export function totalContaPagar(
  itens: readonly Pick<ItemConta, "quantidade" | "valorUnitarioCentavos">[],
  avulsoCentavos: Centavos | null,
): Centavos {
  return (avulsoCentavos ?? 0) + itens.reduce((a, it) => a + subtotalItem(it), 0);
}

export interface DadosPagamento {
  data: DataISO;
  valorCentavos: Centavos;
}

// --- derivações ------------------------------------------------------------

/** Os Pagamentos de uma Conta, do mais antigo para o mais recente. */
export function pagamentosDaConta(snapshot: Snapshot, contaId: Id): Pagamento[] {
  return snapshot.pagamentos
    .filter((p) => p.contaId === contaId)
    .sort((a, b) => a.data.localeCompare(b.data));
}

/** Quanto já foi pago/recebido nesta Conta. */
export function totalPago(snapshot: Snapshot, contaId: Id): Centavos {
  return snapshot.pagamentos
    .filter((p) => p.contaId === contaId)
    .reduce((soma, p) => soma + p.valorCentavos, 0);
}

/** Quanto falta para quitar. Nunca negativo. */
export function valorEmAberto(snapshot: Snapshot, conta: Conta): Centavos {
  return Math.max(0, conta.valorCentavos - totalPago(snapshot, conta.id));
}

/**
 * A Situação de Pagamento.
 *
 * Uma Conta de valor zero nasce **quitada**: não há o que pagar, e chamá-la de
 * pendente faria a tela cobrar para sempre uma dívida que não existe.
 */
export function situacaoPagamento(
  snapshot: Snapshot,
  conta: Conta,
): SituacaoPagamento {
  const pago = totalPago(snapshot, conta.id);
  if (pago >= conta.valorCentavos) return "quitada";
  if (pago <= 0) return "pendente";
  return "parcial";
}

/** A Conta venceu e ainda não foi quitada? */
export function contaVencida(
  snapshot: Snapshot,
  conta: Conta,
  hoje: DataISO,
): boolean {
  return conta.vencimento < hoje && situacaoPagamento(snapshot, conta) !== "quitada";
}

// --- validação -------------------------------------------------------------

function exigirConta(snapshot: Snapshot, id: Id): Conta {
  const c = snapshot.contas.find((x) => x.id === id);
  if (!c) throw new ErroConta("Conta não encontrada.");
  return c;
}

function validar(dados: DadosConta, snapshot: Snapshot): Omit<Conta, "id"> {
  if (!(DIRECOES_CONTA as readonly string[]).includes(dados.direcao)) {
    throw new ErroConta("Direção da conta inválida.");
  }
  const descricao = dados.descricao.trim();
  if (descricao === "") throw new ErroConta("Informe a descrição da conta.");

  if (!isDataISO(dados.vencimento)) throw new ErroConta("Informe o vencimento.");

  const categoria = snapshot.categoriasFinanceiras.find(
    (c) => c.id === dados.categoriaId,
  );
  if (!categoria) throw new ErroConta("Escolha uma categoria financeira.");
  if (categoria.aplicaA !== "ambos" && categoria.aplicaA !== dados.direcao) {
    throw new ErroConta(`A categoria "${categoria.nome}" não se aplica a contas a ${dados.direcao}.`);
  }

  // Fonte só existe em Conta a Receber. Numa Conta a Pagar, insistir nela é
  // erro de quem chamou — não silenciamos jogando fora.
  if (dados.direcao === "pagar") {
    if (dados.fonte != null || dados.clienteId != null) {
      throw new ErroConta("Conta a pagar não tem fonte.");
    }
  } else {
    if (dados.fonte == null) throw new ErroConta("Escolha a fonte do recebimento.");
    if (!(FONTES_CONTA as readonly string[]).includes(dados.fonte)) {
      throw new ErroConta("Fonte do recebimento inválida.");
    }
    if (dados.fonte === "cliente") {
      if (!snapshot.clientes.some((c) => c.id === dados.clienteId)) {
        throw new ErroConta("Escolha o cliente da conta a receber.");
      }
    } else if (dados.clienteId != null) {
      throw new ErroConta("Só a fonte \"cliente\" leva um cliente.");
    }
  }

  const fornecedor = dados.fornecedor ? dados.fornecedor.trim() || null : null;
  const observacao = dados.observacao ? dados.observacao.trim() : "";
  const itens: ItemConta[] = [];
  if (Array.isArray(dados.itens)) {
    for (const it of dados.itens) {
      if (!it.insumoId && !it.unidade && !it.quantidade && !it.valorUnitarioCentavos) {
        continue;
      }
      if (
        typeof it.quantidade === "number" &&
        (!Number.isFinite(it.quantidade) || it.quantidade < 0)
      ) {
        throw new ErroConta("Quantidade do item não pode ser negativa.");
      }
      if (
        typeof it.valorUnitarioCentavos === "number" &&
        (!Number.isInteger(it.valorUnitarioCentavos) || it.valorUnitarioCentavos < 0)
      ) {
        throw new ErroConta("Valor unitário do item inválido.");
      }
      itens.push({
        insumoId: it.insumoId,
        unidade: it.unidade ?? "",
        quantidade: it.quantidade ?? 0,
        valorUnitarioCentavos: it.valorUnitarioCentavos ?? 0,
      });
    }
  }

  // Valor: na Conta a Pagar, calculado (avulso + itens); na a Receber, o informado.
  const valido = (n: unknown): n is number => typeof n === "number" && Number.isInteger(n) && n >= 0;
  let valorCentavos: Centavos;
  let avulsoCentavos: Centavos | null = null;
  if (dados.direcao === "pagar") {
    avulsoCentavos = dados.avulsoCentavos ?? null;
    if ((avulsoCentavos != null && !valido(avulsoCentavos)) || (avulsoCentavos == null && itens.length === 0)) {
      throw new ErroConta("Informe um valor válido.");
    }
    valorCentavos = totalContaPagar(itens, avulsoCentavos);
  } else {
    if (!valido(dados.valorCentavos)) throw new ErroConta("Informe um valor válido.");
    valorCentavos = dados.valorCentavos;
  }

  return {
    direcao: dados.direcao,
    descricao,
    valorCentavos,
    avulsoCentavos,
    vencimento: dados.vencimento,
    categoriaId: dados.categoriaId,
    fonte: dados.direcao === "pagar" ? null : dados.fonte,
    clienteId: dados.fonte === "cliente" ? dados.clienteId : null,
    faturaId: dados.faturaId ?? null,
    fornecedor,
    observacao,
    itens,
  };
}

// --- comandos do domínio ---------------------------------------------------

export function criarConta(
  snapshot: Snapshot,
  dados: DadosConta,
  /** Id gerado no cliente (ADR-0003). */
  id: Id = newId(),
): Snapshot {
  // Trava de segurança: Fatura só pode gerar uma Conta (ADR-0011).
  if (dados.faturaId != null && contaDaFatura(snapshot, dados.faturaId)) {
    throw new ErroConta("Esta fatura já possui uma conta a receber.");
  }
  const conta: Conta = { id, ...validar(dados, snapshot) };
  return { ...snapshot, contas: [...snapshot.contas, conta] };
}

export function editarConta(snapshot: Snapshot, id: Id, dados: DadosConta): Snapshot {
  const atual = exigirConta(snapshot, id);
  const campos = validar(dados, snapshot);
  if (campos.direcao !== atual.direcao) {
    throw new ErroConta("Não dá para trocar uma conta a pagar por uma a receber.");
  }
  // Conta gerada por Fatura: valor e Fonte espelham a Fatura e não se editam
  // aqui (ADR-0011). Vencimento e Pagamentos, sim.
  if (atual.faturaId != null) {
    if (campos.valorCentavos !== atual.valorCentavos) {
      throw new ErroConta("O valor vem da fatura. Reabra a fatura para mudá-lo.");
    }
    if (campos.fonte !== atual.fonte || campos.clienteId !== atual.clienteId) {
      throw new ErroConta("A fonte vem da fatura e não pode ser trocada.");
    }
  }
  // Baixar o valor abaixo do que já foi pago deixaria a Conta "quitada" com
  // troco — estado que não existe no domínio.
  const pago = totalPago(snapshot, id);
  if (campos.valorCentavos < pago) {
    throw new ErroConta(
      "O valor não pode ficar abaixo do que já foi pago. Remova um pagamento antes.",
    );
  }
  return {
    ...snapshot,
    contas: snapshot.contas.map((c) => (c.id === id ? { ...c, ...campos } : c)),
  };
}

/** Exclui a Conta e os Pagamentos dela — não deixa Pagamento órfão. */
export function excluirConta(snapshot: Snapshot, id: Id): Snapshot {
  const atual = exigirConta(snapshot, id);
  if (atual.faturaId != null) {
    throw new ErroConta(
      "Esta conta veio de uma fatura. Reabra a fatura para removê-la.",
    );
  }
  return {
    ...snapshot,
    contas: snapshot.contas.filter((c) => c.id !== id),
    pagamentos: snapshot.pagamentos.filter((p) => p.contaId !== id),
  };
}

export function registrarPagamento(
  snapshot: Snapshot,
  contaId: Id,
  dados: DadosPagamento,
  id: Id = newId(),
): Snapshot {
  const conta = exigirConta(snapshot, contaId);
  if (!isDataISO(dados.data)) throw new ErroConta("Informe a data do pagamento.");
  if (!Number.isInteger(dados.valorCentavos) || dados.valorCentavos <= 0) {
    throw new ErroConta("Informe um valor de pagamento maior que zero.");
  }
  const emAberto = valorEmAberto(snapshot, conta);
  if (dados.valorCentavos > emAberto) {
    throw new ErroConta(
      emAberto === 0
        ? "Esta conta já está quitada."
        : "O pagamento não pode passar o valor em aberto da conta.",
    );
  }
  const p: Pagamento = {
    id,
    contaId,
    data: dados.data,
    valorCentavos: dados.valorCentavos,
  };
  return { ...snapshot, pagamentos: [...snapshot.pagamentos, p] };
}

export function removerPagamento(snapshot: Snapshot, pagamentoId: Id): Snapshot {
  if (!snapshot.pagamentos.some((p) => p.id === pagamentoId)) {
    throw new ErroConta("Pagamento não encontrado.");
  }
  return {
    ...snapshot,
    pagamentos: snapshot.pagamentos.filter((p) => p.id !== pagamentoId),
  };
}

/** A Conta a Receber gerada por uma Fatura, se houver (ADR-0011). */
export function contaDaFatura(snapshot: Snapshot, faturaId: Id): Conta | null {
  return snapshot.contas.find((c) => c.faturaId === faturaId) ?? null;
}

/** Remoção interna, usada pela reabertura da Fatura — passa por cima da trava. */
export function removerContaDaFatura(snapshot: Snapshot, faturaId: Id): Snapshot {
  const conta = contaDaFatura(snapshot, faturaId);
  if (!conta) return snapshot;
  return {
    ...snapshot,
    contas: snapshot.contas.filter((c) => c.id !== conta.id),
    pagamentos: snapshot.pagamentos.filter((p) => p.contaId !== conta.id),
  };
}
